import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Heading, Paragraph, Table, Tag } from '@digdir/designsystemet-react';
import type { HrTeamSummary } from '../../../../api/queries';
import { Routes } from '../../../../enums/Routes';
import { buildRoute } from '../../../../utils/routeBuilder';
import {
  changeSincePrevious,
  formatScore,
  MISSING_MEASUREMENT_DAYS,
  scoreLevel,
  scorePercent,
  TeamFilter,
  teamStatus,
} from '../hrPsychologicalSafetyView';
import { agreementOf } from '../../../../domain/psychological-safety/team-result';
import classes from '../HRPsychologicalSafety.module.css';

const VISIBLE_MISSING = 8;

type TeamsSectionProps = {
  filter: TeamFilter;
  onFilterChange: (filter: TeamFilter) => void;
  withResults: HrTeamSummary[];
  followUp: HrTeamSummary[];
  missing: HrTeamSummary[];
  /** Organisasjonsadministratorer kan planlegge måling for teamet */
  canPlan: boolean;
};

const TeamRow = ({ team }: { team: HrTeamSummary }) => {
  const { t, i18n } = useTranslation();
  const score = team.currentScore ?? 0;
  const status = teamStatus(team);
  const agreement = agreementOf(team.currentStandardDeviation);
  const change = changeSincePrevious(team);
  const isOld = team.daysSinceLastMeasurement > MISSING_MEASUREMENT_DAYS;

  return (
    <Table.Row>
      <Table.HeaderCell scope="row" className={classes.teamName}>
        {team.teamName}
      </Table.HeaderCell>
      <Table.Cell>
        {status !== 'none' && (
          <Tag data-size="sm" data-color={status}>
            {t(`hrPsychologicalSafety.status.${status}`)}
          </Tag>
        )}
      </Table.Cell>
      <Table.Cell>
        <span className={classes.scoreCell}>
          <strong>{formatScore(score, i18n.language)}</strong>
          <span aria-hidden="true" className={classes.miniTrack}>
            <span className={classes.fill} data-level={scoreLevel(score)} style={{ width: `${scorePercent(score)}%` }} />
          </span>
        </span>
      </Table.Cell>
      <Table.Cell>
        {agreement ? (
          <>
            {t(`teamResult.agreement.${agreement}`)}{' '}
            <span className={classes.subtle}>
              ({formatScore(team.currentStandardDeviation ?? 0, i18n.language, 2)})
            </span>
          </>
        ) : (
          <span className={classes.subtle}>–</span>
        )}
      </Table.Cell>
      <Table.Cell>
        {change === null ? (
          <span className={classes.subtle}>{t('hrPsychologicalSafety.teams.noPrevious')}</span>
        ) : (
          <span className={classes.change} data-tone={change > 0 ? 'positive' : change < 0 ? 'negative' : 'neutral'}>
            <span aria-hidden="true">{change > 0 ? '↑ ' : change < 0 ? '↓ ' : ''}</span>
            <span className="ds-sr-only">
              {t(`hrPsychologicalSafety.teams.change.${change > 0 ? 'up' : change < 0 ? 'down' : 'same'}`)}
            </span>
            {formatScore(Math.abs(change), i18n.language)}
          </span>
        )}
      </Table.Cell>
      <Table.Cell className={isOld ? classes.old : undefined}>
        {t('hrPsychologicalSafety.teams.daysAgo', { count: team.daysSinceLastMeasurement })}
      </Table.Cell>
      <Table.Cell className={classes.numberCell}>
        {team.respondentCount ?? '–'} / {team.teamMemberCount}
      </Table.Cell>
    </Table.Row>
  );
};

/** Teamene med resultater, de som trenger oppfølging og de som ikke har målt */
export const TeamsSection = ({ filter, onFilterChange, withResults, followUp, missing, canPlan }: TeamsSectionProps) => {
  const { t } = useTranslation();
  const [showAllMissing, setShowAllMissing] = useState(false);
  const filters: { key: TeamFilter; count: number }[] = [
    { key: 'results', count: withResults.length },
    { key: 'followUp', count: followUp.length },
    { key: 'missing', count: missing.length },
  ];
  const rows = filter === 'followUp' ? followUp : withResults;
  const visibleMissing = showAllMissing ? missing : missing.slice(0, VISIBLE_MISSING);

  const missingReason = (team: HrTeamSummary) =>
    [
      team.lastMeasurementDate === null
        ? t('hrPsychologicalSafety.teams.neverMeasured')
        : t('hrPsychologicalSafety.teams.lastMeasured', { count: team.daysSinceLastMeasurement }),
      team.hasScheduledSurvey ? '' : t('hrPsychologicalSafety.teams.nothingPlanned'),
    ]
      .filter(Boolean)
      .join(' · ');

  return (
    <section aria-labelledby="hr-ps-teams-heading" id="hr-ps-teams" className={classes.section}>
      <div className={classes.sectionIntro}>
        <Heading level={2} data-size="md" id="hr-ps-teams-heading">
          {t('hrPsychologicalSafety.teams.title')}
        </Heading>
        <Paragraph className={classes.subtle}>{t(`hrPsychologicalSafety.teams.help.${filter}`)}</Paragraph>
      </div>

      <div role="group" aria-label={t('hrPsychologicalSafety.teams.filterLabel')} className={classes.filters}>
        {filters.map(({ key, count }) => (
          <button
            key={key}
            type="button"
            className={classes.filter}
            aria-pressed={filter === key}
            onClick={() => onFilterChange(key)}
          >
            {t(`hrPsychologicalSafety.teams.filters.${key}`)}
            <span className={classes.filterCount}>{count}</span>
          </button>
        ))}
      </div>

      {filter === 'missing' ? (
        missing.length === 0 ? (
          <Paragraph className={classes.subtle}>{t('hrPsychologicalSafety.teams.empty.missing')}</Paragraph>
        ) : (
          <>
            <ul className={`${classes.card} ${classes.missingList}`}>
              {visibleMissing.map((team) => (
                <li key={team.teamId} className={classes.missingRow}>
                  <div className={classes.missingText}>
                    <span className={classes.teamName}>{team.teamName}</span>
                    <span className={classes.subtle}>{missingReason(team)}</span>
                  </div>
                  {canPlan && (
                    <Button asChild variant="secondary" data-size="sm">
                      <Link
                        to={buildRoute(Routes.OrganizationTeamScheduling, { teamId: team.teamId })}
                        aria-label={t('hrPsychologicalSafety.teams.planFor', { name: team.teamName })}
                      >
                        {t('hrPsychologicalSafety.teams.plan')}
                      </Link>
                    </Button>
                  )}
                </li>
              ))}
            </ul>
            {missing.length > VISIBLE_MISSING && (
              <div>
                <Button
                  variant="tertiary"
                  aria-expanded={showAllMissing}
                  onClick={() => setShowAllMissing((show) => !show)}
                >
                  {showAllMissing ? t('hrPsychologicalSafety.teams.showFewer') : t('hrPsychologicalSafety.teams.showMore')}
                </Button>
              </div>
            )}
          </>
        )
      ) : rows.length === 0 ? (
        <Paragraph className={classes.subtle}>{t(`hrPsychologicalSafety.teams.empty.${filter}`)}</Paragraph>
      ) : (
        <div className={classes.tableScroll}>
          <Table className={classes.table}>
            <Table.Head>
              <Table.Row>
                {['team', 'status', 'score', 'agreement', 'change', 'lastMeasured', 'responses'].map((column) => (
                  <Table.HeaderCell key={column} className={column === 'responses' ? classes.numberCell : undefined}>
                    {t(`hrPsychologicalSafety.teams.columns.${column}`)}
                  </Table.HeaderCell>
                ))}
              </Table.Row>
            </Table.Head>
            <Table.Body>
              {rows.map((team) => (
                <TeamRow key={team.teamId} team={team} />
              ))}
            </Table.Body>
          </Table>
        </div>
      )}
    </section>
  );
};
